import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getTaskById, updateTask } from '../../../../api/task';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '../../../../components/ui/form';
import { getProjectMembers } from '../../../../api/project';
import { useProjects } from '../../../../hooks/project';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import toastifyInstance from '../../../../utils/toastify';
import { Switch } from '@/components/ui/switch';

// Validation schema (same as create task)
const updateTaskSchema = z.object({
    name: z.string().min(1, "Task name is required"),
    description: z.string().optional(),
    projectId: z.string().min(1, "Project ID is required"),
    uid: z.string().min(1, "Assign to is required"),
    isActive: z.boolean().optional()
});

const UpdateTask: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const taskId = searchParams.get('taskId');

    // Fetch task details
    const { data: taskData, isLoading: isTaskLoading } = useQuery({
        queryKey: ['task', taskId],
        queryFn: () => getTaskById(taskId || ''),
        enabled: !!taskId
    });

    const form = useForm<z.infer<typeof updateTaskSchema>>({
        resolver: zodResolver(updateTaskSchema),
        defaultValues: {
            name: taskData?.name || '',
            description: taskData?.description || '',
            projectId: taskData?.projectId || '',
            uid: taskData?.uid || '',
            isActive: taskData?.isActive || true
        }
    });

    // Update form values when task data is loaded
    useEffect(() => {
        if (taskData) {
            form.reset({
                name: taskData.name,
                description: taskData.description || '',
                projectId: taskData.projectId || '',
                uid: taskData.uid || ''
            });
        }
    }, [taskData, form]);

    const projectId = form.watch('projectId');

    const { projects } = useProjects();

    const { data: projectMembers } = useQuery({
        queryKey: ['projectMembers', projectId],
        queryFn: () => {
            if (projectId) {
                return getProjectMembers(projectId, true);
            }
            return [];
        }
    });

    const mutation = useMutation({
        mutationFn: updateTask,
        onSuccess: () => {
            toastifyInstance.success('Task updated successfully');
            navigate('/tasks');
        },
        onError: () => {
            toastifyInstance.error('Failed to update task');
        }
    });

    const onSubmit = (values: z.infer<typeof updateTaskSchema>) => {
        if (taskId) {
            mutation.mutate({
                _id: taskId,
                ...values,
                description: values.description || '',
                uid: values.uid || '',
                isActive: values?.isActive || true,
            });
        }
    };

    if (isTaskLoading) return <div>Loading...</div>;

    return (
        <div className="w-full max-w-md">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Task Name</FormLabel>
                                <FormControl>
                                    <Input placeholder="Enter task name" {...field} />
                                </FormControl>
                                <FormMessage className='text-red-500' />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                    <Input placeholder="Enter task description" {...field} />
                                </FormControl>
                                <FormMessage className='text-red-500' />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="projectId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Project</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                    >
                                        <FormItem>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select project" />
                                                </SelectTrigger>
                                            </FormControl>
                                        </FormItem>
                                        <SelectContent>
                                            {projects?.map((project) => {
                                                if (!project._id) return null;
                                                return (
                                                    <SelectItem key={project._id} value={project._id}>
                                                        {project.name}
                                                    </SelectItem>
                                                )
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage className='text-red-500' />
                            </FormItem>
                        )}
                    />

                    {
                        projectId && projectMembers?.length > 0 ? (
                            <FormField
                                control={form.control}
                                name="uid"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Assign To</FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                defaultValue={field.value}
                                            >
                                                <FormItem>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select assign to" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                </FormItem>
                                                <SelectContent>
                                                    {projectMembers?.map((member: any) => (
                                                        <SelectItem key={member._id} value={member._id}>
                                                            {member.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage className='text-red-500' />
                                    </FormItem>
                                )}
                            />
                        ) : (
                            <div className="text-red-500">
                                No members found
                            </div>
                        )
                    }

                    <FormField
                        control={form.control}
                        name="isActive"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className='flex items-center gap-2'>Status
                                    <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormLabel>
                            </FormItem>
                        )}
                    />

                    <div className="flex justify-between">
                        <Button type="button" variant="outline" onClick={() => navigate('/tasks')}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={mutation.isPending}>
                            {mutation.isPending ? 'Updating...' : 'Update Task'}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
};

export default UpdateTask;